import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Video, Quote } from 'lucide-react';
import StarRating from '../components/testimonials/StarRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTestimonialView() {
    const { submissionId } = useParams();
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/testimonials/public/view/${submissionId}`);
                setSubmission(res.data);
            } catch {
                setError('Testimonial not found');
            } finally { setLoading(false); }
        })();
    }, [submissionId]);

    // Send height to parent for embed resize
    useEffect(() => {
        if (isEmbed && submission) {
            const sendHeight = () => {
                window.parent.postMessage({
                    type: 'testimonial-resize',
                    height: document.body.scrollHeight,
                }, '*');
            };
            sendHeight();
            const observer = new ResizeObserver(sendHeight);
            observer.observe(document.body);
            return () => observer.disconnect();
        }
    }, [isEmbed, submission]);

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
    );

    if (error || !submission) return (
        <div className="flex items-center justify-center h-64 text-stone-400 text-sm">{error}</div>
    );

    const textAnswer = submission.answers
        ? Object.values(submission.answers).find(v => typeof v === 'string' && v.length > 10)
        : null;

    return (
        <div className={`${isEmbed ? '' : 'min-h-screen bg-stone-50 flex items-center justify-center p-6'}`}>
            <div className={`bg-white rounded-xl ${isEmbed ? '' : 'shadow-lg border border-stone-200 max-w-lg w-full'} overflow-hidden`}>
                {/* Video */}
                {submission.video_url && (
                    <div className="bg-stone-900">
                        <video src={submission.video_url} controls playsInline className="w-full" />
                    </div>
                )}

                <div className="p-5 space-y-3">
                    {/* Rating */}
                    {submission.overall_rating > 0 && (
                        <StarRating value={submission.overall_rating} maxStars={5} size="md" readonly />
                    )}

                    {/* Text quote */}
                    {textAnswer && (
                        <div className="relative">
                            <Quote className="w-6 h-6 text-stone-200 absolute -top-1 -left-1" />
                            <p className="text-stone-700 text-sm leading-relaxed pl-5 italic">"{textAnswer}"</p>
                        </div>
                    )}

                    {/* Attribution */}
                    <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                        <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-xs font-medium text-stone-600">
                            {(submission.contact_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div className="text-sm font-medium text-stone-800">{submission.contact_name}</div>
                            {submission.contact_email && (
                                <div className="text-xs text-stone-400">{submission.contact_email}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
